/**
 * Current user's quiz assignments and scenario steps, plus the derived open count (sidebar
 * link, home banner). Fed by useMyQuizAssignments and useMyScenarioSteps, both mounted once
 * in AuthenticatedLayout.
 */

import { create } from 'zustand';
import type { QuizAssignment, ScenarioRunStep } from '../types';
import { isOpenAssignment } from '../utils/assignmentStatus';
import { countOpenSteps } from '../utils/scenarioStatus';

interface MyQuizzesState {
  assignments: QuizAssignment[];
  loading: boolean;
  error: string | null;
  /** Scenario steps open to the user (any status: the listener filters on visibleTo). */
  steps: ScenarioRunStep[];
  stepsLoading: boolean;
  stepsError: string | null;
  /** Assignments the user can still act on (assigned / in_progress) */
  openQuizCount: number;
  /** Scenario steps the user can answer now (open / redo) */
  openStepCount: number;
  /** openQuizCount + openStepCount */
  openCount: number;
  /** Epoch ms the open count was last evaluated at; bumped when a start date passes. */
  now: number;
  setAssignments: (assignments: QuizAssignment[]) => void;
  /** Re-evaluate which assignments are available (a start date has passed). */
  setNow: (now: number) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  setSteps: (steps: ScenarioRunStep[]) => void;
  setStepsLoading: (loading: boolean) => void;
  setStepsError: (error: string | null) => void;
  reset: () => void;
}

const countOpen = (assignments: QuizAssignment[], now: number) =>
  assignments.filter((a) => isOpenAssignment(a, now)).length;

export const useMyQuizzesStore = create<MyQuizzesState>((set) => ({
  assignments: [],
  loading: true,
  error: null,
  steps: [],
  stepsLoading: true,
  stepsError: null,
  openQuizCount: 0,
  openStepCount: 0,
  openCount: 0,
  now: Date.now(),
  setAssignments: (assignments) => {
    const now = Date.now();
    set((s) => {
      const openQuizCount = countOpen(assignments, now);
      return { assignments, now, openQuizCount, openCount: openQuizCount + s.openStepCount, loading: false, error: null };
    });
  },
  setNow: (now) =>
    set((s) => {
      const openQuizCount = countOpen(s.assignments, now);
      return { now, openQuizCount, openCount: openQuizCount + s.openStepCount };
    }),
  setLoading: (loading) => set({ loading }),
  setError: (error) => set({ error, loading: false }),
  setSteps: (steps) =>
    set((s) => {
      const openStepCount = countOpenSteps(steps);
      return { steps, openStepCount, openCount: s.openQuizCount + openStepCount, stepsLoading: false, stepsError: null };
    }),
  setStepsLoading: (stepsLoading) => set({ stepsLoading }),
  setStepsError: (stepsError) => set({ stepsError, stepsLoading: false }),
  reset: () =>
    set({
      assignments: [],
      loading: true,
      error: null,
      steps: [],
      stepsLoading: true,
      stepsError: null,
      openQuizCount: 0,
      openStepCount: 0,
      openCount: 0,
      now: Date.now(),
    }),
}));

export const selectNow = (s: MyQuizzesState) => s.now;
export const selectOpenCount = (s: MyQuizzesState) => s.openCount;
